import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  AlertCircle,
  CreditCard,
  Headphones,
  LifeBuoy,
  SendHorizonal,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { trackProductEvent } from "../lib/analytics";

const CATEGORIES = [
  {
    id: "payment",
    label: "Payment Issue",
    icon: CreditCard,
    desc: "Subscriptions, charge problems, cancellations.",
  },
  {
    id: "feature",
    label: "Feature Issue",
    icon: Wrench,
    desc: "A page, tool, or product feature is not working.",
  },
  {
    id: "account",
    label: "Account Help",
    icon: LifeBuoy,
    desc: "Login, access, profile, or plan related support.",
  },
  {
    id: "other",
    label: "Other Issue",
    icon: AlertCircle,
    desc: "Anything else you want the team to check.",
  },
];

const Page = styled.main`
  min-height: 100vh;
  padding: 110px 20px 90px;
  color: #e5e7eb;
  background:
    radial-gradient(860px at 12% 10%, rgba(245, 158, 11, 0.14), transparent 58%),
    radial-gradient(920px at 88% 12%, rgba(56, 189, 248, 0.14), transparent 60%),
    linear-gradient(180deg, #020617 0%, #04111f 100%);
`;

const Shell = styled.div`
  max-width: 1240px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 30px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.84)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.12), transparent 32%);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.34);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: clamp(36px, 5vw, 56px);
  line-height: 1.04;
`;

const Sub = styled.p`
  margin: 16px 0 0;
  max-width: 760px;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1.8;
`;

const MainGrid = styled.section`
  margin-top: 22px;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.92);
  overflow: hidden;
  min-width: 0;
`;

const Toolbar = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const PanelTitle = styled.div`
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 900;
`;

const PanelBody = styled.div`
  padding: 18px;
`;

const PanelSub = styled.p`
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.7;
`;

const ThreadMetaBar = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.34);
`;

const ThreadMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ThreadPill = styled.div`
  padding: 8px 11px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.52);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const CategoryList = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 10px;
`;

const CategoryButton = styled.button`
  padding: 14px;
  border-radius: 18px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(56, 189, 248, 0.34)" : "rgba(148, 163, 184, 0.12)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(245, 158, 11, 0.1))"
      : "rgba(15, 23, 42, 0.46)"};
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const CategoryTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryText = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.65;
`;

const Input = styled.input`
  width: 100%;
  min-height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
  padding: 0 14px;
  outline: none;
  margin-top: 16px;

  &::placeholder {
    color: #64748b;
  }
`;

const TicketList = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 10px;
`;

const TicketButton = styled.button`
  padding: 14px;
  border-radius: 18px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(56, 189, 248, 0.34)" : "rgba(148, 163, 184, 0.12)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(245, 158, 11, 0.1))"
      : "rgba(15, 23, 42, 0.42)"};
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const TicketTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const TicketMeta = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  color: #94a3b8;
  font-size: 12px;
`;

const TicketMetaLeft = styled.div`
  display: grid;
  gap: 6px;
`;

const TicketBadge = styled.div`
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $pending }) =>
    $pending ? "rgba(245, 158, 11, 0.14)" : "rgba(34, 197, 94, 0.12)"};
  border: 1px solid
    ${({ $pending }) =>
      $pending ? "rgba(245, 158, 11, 0.24)" : "rgba(34, 197, 94, 0.22)"};
  color: ${({ $pending }) => ($pending ? "#fde68a" : "#86efac")};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MessageList = styled.div`
  height: 520px;
  overflow: auto;
  padding: 18px;
  background: rgba(2, 6, 23, 0.72);
  display: grid;
  align-content: start;
  gap: 14px;

  @media (max-width: 960px) {
    height: 420px;
  }
`;

const EmptyState = styled.div`
  padding: 22px;
  border-radius: 18px;
  border: 1px dashed rgba(148, 163, 184, 0.18);
  color: #94a3b8;
  line-height: 1.75;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  align-items: flex-start;
`;

const Bubble = styled.div`
  max-width: min(520px, 100%);
  padding: 14px 16px;
  border-radius: 18px;
  background: ${({ $mine }) =>
    $mine
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.16), rgba(56, 189, 248, 0.12))"
      : "rgba(15, 23, 42, 0.56)"};
  border: 1px solid
    ${({ $mine }) =>
      $mine ? "rgba(245, 158, 11, 0.24)" : "rgba(148, 163, 184, 0.12)"};
  box-shadow: ${({ $mine }) =>
    $mine ? "0 18px 34px rgba(15, 23, 42, 0.18)" : "none"};
  align-self: flex-start;
`;

const BubbleMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const BubbleAuthor = styled.div`
  color: ${({ $mine }) => ($mine ? "#fde68a" : "#7dd3fc")};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BubbleCategory = styled.div`
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.34);
  color: #94a3b8;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BubbleText = styled.div`
  margin-top: 8px;
  color: #f8fafc;
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-wrap;
`;

const BubbleTime = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
`;

const Composer = styled.div`
  padding: 16px 18px 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.94);
`;

const ComposerCard = styled.div`
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.42);
`;

const ComposerLabel = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ComposerHint = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.65;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #dbeafe;
  padding: 14px;
  outline: none;
  resize: vertical;
  line-height: 1.75;

  &::placeholder {
    color: #64748b;
  }
`;

const ComposerRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CharacterCount = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
`;

const Button = styled.button`
  min-height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  border: ${({ $primary }) =>
    $primary ? "none" : "1px solid rgba(148, 163, 184, 0.14)"};
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #f59e0b, #38bdf8)"
      : "rgba(15, 23, 42, 0.56)"};
  color: ${({ $primary }) => ($primary ? "#04111f" : "#f8fafc")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Status = styled.div`
  padding: 14px 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  color: ${({ $error }) => ($error ? "#fecaca" : "#94a3b8")};
  background: ${({ $error }) =>
    $error ? "rgba(127, 29, 29, 0.24)" : "rgba(15, 23, 42, 0.32)"};
  font-size: 13px;
  line-height: 1.7;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: min(760px, 100%);
  max-height: min(88vh, 860px);
  overflow: auto;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(12, 18, 30, 0.96), rgba(2, 6, 23, 0.94)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.12), transparent 32%);
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.44);
`;

const ModalHeader = styled.div`
  padding: 22px 22px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const ModalTitle = styled.div`
  color: #f8fafc;
  font-size: 1.35rem;
  font-weight: 900;
`;

const ModalSub = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.75;
  max-width: 540px;
`;

const CloseButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const LoginPrompt = styled.div`
  margin-top: 22px;
  padding: 20px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.92);
  color: #94a3b8;
  line-height: 1.8;
`;

const ticketsCollection = collection(db, "tickets");
const ticketRefFor = (ticketId) => doc(db, "tickets", ticketId);
const messagesRefFor = (ticketId) =>
  collection(db, "tickets", ticketId, "messages");

export default function SupportCenterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedCategory, setSelectedCategory] = useState("payment");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [status, setStatus] = useState(
    "Choose a ticket type and start your support conversation."
  );
  const [hasError, setHasError] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setThreads([]);
      setActiveThreadId(null);
      setMessages([]);
      return undefined;
    }

    const unsubscribeThreads = onSnapshot(
      query(ticketsCollection, where("uid", "==", user.uid)),
      (snap) => {
        const nextThreads = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const aMs = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
            const bMs = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
            return bMs - aMs;
          });
        setThreads(nextThreads);
        setActiveThreadId((current) => current || nextThreads[0]?.id || null);
      }
    );

    return () => unsubscribeThreads();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !activeThreadId) {
      setMessages([]);
      return undefined;
    }

    const unsubscribeMessages = onSnapshot(
      query(messagesRefFor(activeThreadId), orderBy("createdAt", "asc")),
      (snap) => {
        setMessages(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
      }
    );

    return () => unsubscribeMessages();
  }, [user?.uid, activeThreadId]);

  const selectedCategoryMeta = useMemo(
    () => CATEGORIES.find((item) => item.id === selectedCategory) || CATEGORIES[0],
    [selectedCategory]
  );

  const activeThread = useMemo(
    () => threads.find((item) => item.id === activeThreadId) || null,
    [threads, activeThreadId]
  );

  const adminHasReplied = useMemo(
    () => messages.some((item) => item.senderType === "admin"),
    [messages]
  );

  useEffect(() => {
    if (!activeThread) return;
    setSelectedCategory(activeThread.category || "payment");
    setSubject(activeThread.subject || "");
  }, [activeThread]);

  const openNewIssue = () => {
    setActiveThreadId(null);
    setSelectedCategory("payment");
    setSubject("");
    setMessage("");
    setMessages([]);
    setIssueModalOpen(true);
    setStatus("New ticket mode. Choose a category and send your first message.");
    setHasError(false);
  };

  const confirmNewIssue = () => {
    const normalizedSubject = subject.trim() || selectedCategoryMeta.label;
    setSubject(normalizedSubject);

    setIssueModalOpen(false);
    setStatus(
      "Ticket setup saved. Now write your first message and create the ticket."
    );
    setHasError(false);
  };

  const sendMessage = async () => {
    if (!user?.uid) {
      setStatus("Please log in first to start a support conversation.");
      setHasError(true);
      return;
    }

    if (!message.trim()) {
      setStatus("Please write your issue message first.");
      setHasError(true);
      return;
    }

    setSending(true);
    try {
      let targetThreadId = activeThreadId;

      if (!targetThreadId) {
        const createdThread = await addDoc(ticketsCollection, {
          uid: user.uid,
          name: user.name || "",
          email: user.email || "",
          category: selectedCategory,
          categoryLabel: selectedCategoryMeta.label,
          subject: subject.trim() || selectedCategoryMeta.label,
          status: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        targetThreadId = createdThread.id;
        setActiveThreadId(createdThread.id);

        void trackProductEvent(
          "support_ticket_created",
          {
            ticketId: createdThread.id,
            category: selectedCategory,
            categoryLabel: selectedCategoryMeta.label,
            subject: subject.trim() || selectedCategoryMeta.label,
          },
          { user }
        );
      } else {
        await updateDoc(ticketRefFor(targetThreadId), {
          category: selectedCategory,
          categoryLabel: selectedCategoryMeta.label,
          subject: subject.trim() || selectedCategoryMeta.label,
          updatedAt: serverTimestamp(),
        });
      }

      await addDoc(messagesRefFor(targetThreadId), {
        senderType: "user",
        senderName: user.name || "You",
        text: message.trim(),
        category: selectedCategory,
        createdAt: serverTimestamp(),
      });

      await updateDoc(ticketRefFor(targetThreadId), {
        updatedAt: serverTimestamp(),
        status: "pending",
      });

      void trackProductEvent(
        "support_ticket_message_sent",
        {
          ticketId: targetThreadId,
          category: selectedCategory,
          isFirstMessage: !activeThreadId,
        },
        { user }
      );

      setMessage("");
      setStatus(
        "Ticket message sent. This conversation will stay pending until support replies."
      );
      setHasError(false);
    } catch (error) {
      setStatus(`Could not send your ticket right now: ${error.message}`);
      setHasError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Page>
      <Shell>
        <Hero>
          <Eyebrow>
            <Headphones size={14} />
            Support center
          </Eyebrow>
          <Title>Report payment or feature tickets and chat with support</Title>
          <Sub>
            Create a new ticket whenever you need help. Each ticket becomes its own
            conversation and stays pending until the support team replies.
          </Sub>
        </Hero>

        {!user ? (
          <LoginPrompt>
            Log in to start a support conversation with the team. Once you are
            signed in, you can report payment problems, feature bugs, and account
            tickets here.
            <div style={{ marginTop: 14 }}>
              <Button $primary onClick={() => navigate("/")}>
                <Sparkles size={15} />
                Go to Home
              </Button>
            </div>
          </LoginPrompt>
        ) : (
          <MainGrid>
            <Panel>
              <Toolbar>
                <PanelTitle>Your tickets</PanelTitle>
                <Button onClick={openNewIssue}>New Ticket</Button>
              </Toolbar>
              <PanelBody>
                <PanelSub>
                  Open an existing ticket or create a new one. Each ticket stays in
                  its own conversation thread.
                </PanelSub>
                <TicketList>
                  {threads.length ? (
                    threads.map((item) => (
                      <TicketButton
                        key={item.id}
                        $active={activeThreadId === item.id}
                        onClick={() => setActiveThreadId(item.id)}
                      >
                        <TicketTitle>
                          {item.subject || item.categoryLabel || "Ticket"}
                        </TicketTitle>
                        <TicketMeta>
                          <TicketMetaLeft>
                            <span>{item.categoryLabel || "General"}</span>
                            <span>
                              {item.createdAt?.toDate
                                ? `Created ${item.createdAt
                                    .toDate()
                                    .toLocaleString()}`
                                : "Created just now"}
                            </span>
                          </TicketMetaLeft>
                          <TicketBadge $pending={(item.status || "pending") === "pending"}>
                            {(item.status || "pending") === "pending"
                              ? "Pending"
                              : "Replied"}
                          </TicketBadge>
                        </TicketMeta>
                      </TicketButton>
                    ))
                  ) : (
                    <EmptyState>
                      No tickets yet. Create your first support conversation.
                    </EmptyState>
                  )}
                </TicketList>
              </PanelBody>
            </Panel>

            <Panel>
              <Toolbar>
                <PanelTitle>
                  {activeThread?.subject || "Support conversation"}
                </PanelTitle>
                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                  {activeThread
                    ? `Status: ${adminHasReplied ? "replied" : "pending"}`
                  : "No ticket submitted yet"}
                </div>
              </Toolbar>

              {activeThread && (
                <ThreadMetaBar>
                  <ThreadMetaLeft>
                    <ThreadPill>{activeThread.categoryLabel || "General ticket"}</ThreadPill>
                    <ThreadPill>
                      {adminHasReplied ? "Support replied" : "Waiting for admin reply"}
                    </ThreadPill>
                  </ThreadMetaLeft>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Thread ID: {activeThread.id}
                  </div>
                </ThreadMetaBar>
              )}

              <MessageList>
                {messages.length ? (
                  messages.map((item) => {
                    const isMine = item.senderType === "user";
                    const timestamp = item.createdAt?.toDate
                      ? item.createdAt.toDate().toLocaleString()
                      : "Sending...";

                    return (
                      <MessageRow key={item.id} $mine={isMine}>
                        <Bubble $mine={isMine}>
                          <BubbleMeta $mine={isMine}>
                            <BubbleAuthor $mine={isMine}>
                              {isMine ? "You" : item.senderName || "Support Team"}
                            </BubbleAuthor>
                            <BubbleCategory>
                              {item.category || activeThread?.category || "support"}
                            </BubbleCategory>
                          </BubbleMeta>
                          <BubbleText>{item.text}</BubbleText>
                          <BubbleTime>{timestamp}</BubbleTime>
                        </Bubble>
                      </MessageRow>
                    );
                  })
                ) : (
                  <EmptyState>
                    Start a new ticket conversation. Until the support team replies,
                    this ticket will remain pending.
                  </EmptyState>
                )}
              </MessageList>

              <Composer>
                <ComposerCard>
                  <ComposerLabel>
                    {activeThreadId ? "Reply to this ticket" : "Create new ticket message"}
                  </ComposerLabel>
                  <ComposerHint>
                    Mention what happened, where it happened, and what result you expected.
                    Payment tickets should include plan, charge, or cancellation details if relevant.
                  </ComposerHint>
                  <Textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Describe the issue clearly. Mention what happened, where it happened, and what you expected."
                  />
                </ComposerCard>
                <ComposerRow>
                  <div style={{ color: "#94a3b8", fontSize: 12 }}>
                    Each new ticket becomes its own support conversation.
                  </div>
                  <CharacterCount>{message.length}/1500</CharacterCount>
                  <Button $primary onClick={sendMessage} disabled={sending}>
                    <SendHorizonal size={15} />
                    {sending
                      ? "Sending..."
                      : activeThreadId
                      ? "Send Reply"
                      : "Create Ticket"}
                  </Button>
                </ComposerRow>
              </Composer>

              <Status $error={hasError}>{status}</Status>
            </Panel>
          </MainGrid>
        )}

        {issueModalOpen && (
          <ModalOverlay onClick={() => setIssueModalOpen(false)}>
            <ModalCard onClick={(event) => event.stopPropagation()}>
              <ModalHeader>
                <div>
                  <ModalTitle>Create New Ticket</ModalTitle>
                  <ModalSub>
                    Select the ticket type first, then add a short subject so support
                    can identify the conversation quickly.
                  </ModalSub>
                </div>
                <CloseButton onClick={() => setIssueModalOpen(false)}>
                  <X size={18} />
                </CloseButton>
              </ModalHeader>

              <PanelBody>
                <CategoryList>
                  {CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <CategoryButton
                        key={category.id}
                        $active={selectedCategory === category.id}
                        onClick={() => setSelectedCategory(category.id)}
                      >
                        <CategoryTitle>
                          <Icon size={15} />
                          {category.label}
                        </CategoryTitle>
                        <CategoryText>{category.desc}</CategoryText>
                      </CategoryButton>
                    );
                  })}
                </CategoryList>

                <Input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Short ticket subject, for example: Payment charged twice"
                />

                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                  <Button onClick={() => setIssueModalOpen(false)}>Cancel</Button>
                  <Button $primary onClick={confirmNewIssue}>
                    Continue
                  </Button>
                </div>
              </PanelBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </Shell>
    </Page>
  );
}
